import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronsDown, Pin, PinOff } from "lucide-react";
import * as React from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { usePlanGuidance, usePlanKnowledge, usePlanRules, usePlanTools } from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

import {
	PLAN_GROUPS,
	PLAN_LAYERS,
	type PageSource,
	type PlanGroup,
	type PlanPageEntry,
	SOURCE_LABEL,
	dependentsOf,
	pagesOfGroup,
	planPage,
	resolvePage,
} from "../page-registry";
import type { PlanTab } from "../shell/plan-tabs";
import { BlockTitle, GroupHeading, STAGES, type StageKey, StageNo, stage } from "./shared";

/**
 * 图三：页面地图（菜单重组 §八 8.4）。全部读页面注册表：按一级分组，从上到下按层拉通
 * 定义（基础数据）→ 内容（业务配置、知识管理）→ 链路（应答链路）→ 评测（评测配置、评测模式）→ 分析（数据分析）；
 * 层与层之间一组加粗双箭头 ＋ 这一跳的关系（注册表 layers[].flow）。链路总览自己不画（注册表 hideInMap，地图就在它里面）。
 * 每张卡保持紧凑：页面名、来源徽标、回答什么；依赖 ／ 被依赖个数在详情区；点卡片跳页。组头写这一组回答什么（一级的含义在地图里就清楚）。
 * 悬停一张卡（或点右上角图钉固定）：它读的页蓝圈、读它的页琥珀圈、其余变淡，并画出连线；
 * 右侧固定详情区（第三轮，原「一二级架构」并进来）：这页的主要能力（注册表 caps，带 ①～⑦，点了切到运行时流程并高亮那一步）、
 * 它读谁（读什么）、谁读它；另有回答什么全文、这页内容的层分布（来自层字段，没有层的页不显示）、运行时环节的读／管。详情区常驻、固定宽高、跟着滚动吸顶（未选中显示一句引导），内容多了在区内滚动，不撑开页面。
 * 零布局位移：高亮只用 box-shadow（ring）和 opacity，不改边框宽度和内边距；连线 SVG 绝对定位叠在上层，卡片位置量一次缓存、
 * 只在画布尺寸变化时重量；悬停进入延迟 120ms、移出延迟 200ms；鼠标从卡片移进详情区时保持选中，方便点步号。
 */

const SOURCE_TONE: Record<PageSource, BadgeTone> = {
	平台内置: "neutral",
	系统计算: "info",
	客户自定义: "success",
};

const HOVER_IN_MS = 120;
const HOVER_OUT_MS = 200;

type Hl = "self" | "dep" | "user" | "dim" | "none";

function useRelations(active: PlanTab | null) {
	return React.useMemo(() => {
		if (!active) return null;
		const deps = planPage(active).deps;
		const users = dependentsOf(active);
		return {
			deps,
			users,
			depSet: new Set(deps.map((d) => d.page)),
			userSet: new Set(users.map((u) => u.page)),
		};
	}, [active]);
}

/** 悬停带进入 ／ 移出延迟：鼠标划过卡片之间的缝不会连闪 */
function useDelayedHover() {
	const [hover, setHover] = React.useState<PlanTab | null>(null);
	const timer = React.useRef<number | undefined>(undefined);
	React.useEffect(() => () => window.clearTimeout(timer.current), []);
	const enter = React.useCallback((id: PlanTab) => {
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(() => setHover(id), HOVER_IN_MS);
	}, []);
	const leave = React.useCallback(() => {
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(() => setHover(null), HOVER_OUT_MS);
	}, []);
	/** 鼠标移进详情区：取消移出，保持当前选中 */
	const hold = React.useCallback(() => window.clearTimeout(timer.current), []);
	return { hover, enter, leave, hold };
}

type Rect = { x: number; y: number; w: number; h: number };

/** 卡片位置量一次缓存；只在画布尺寸变化（窗口变宽变窄、换行）时重量，悬停期间不重算 */
function useCardRects(
	boxRef: React.RefObject<HTMLDivElement>,
	cardRefs: React.MutableRefObject<Map<PlanTab, HTMLElement>>,
) {
	const [state, setState] = React.useState<{ rects: Map<PlanTab, Rect>; w: number; h: number }>({
		rects: new Map(),
		w: 0,
		h: 0,
	});
	React.useLayoutEffect(() => {
		const box = boxRef.current;
		if (!box) return;
		let last = "";
		const measure = () => {
			const b = box.getBoundingClientRect();
			const key = `${Math.round(b.width)}x${Math.round(b.height)}`;
			if (key === last) return;
			last = key;
			const rects = new Map<PlanTab, Rect>();
			for (const [id, el] of cardRefs.current) {
				const r = el.getBoundingClientRect();
				rects.set(id, { x: r.left - b.left, y: r.top - b.top, w: r.width, h: r.height });
			}
			setState({ rects, w: b.width, h: b.height });
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(box);
		return () => ro.disconnect();
	}, [boxRef, cardRefs]);
	return state;
}

export function PageMap() {
	const { hover, enter, leave, hold } = useDelayedHover();
	const [pinned, setPinned] = React.useState<PlanTab | null>(null);
	const active = hover ?? pinned;
	const rel = useRelations(active);
	const boxRef = React.useRef<HTMLDivElement>(null);
	const cardRefs = React.useRef(new Map<PlanTab, HTMLElement>());
	const geo = useCardRects(boxRef, cardRefs);

	const hlOf = (id: PlanTab): Hl => {
		if (!active || !rel) return "none";
		if (id === active) return "self";
		if (rel.depSet.has(id)) return "dep";
		if (rel.userSet.has(id)) return "user";
		return "dim";
	};

	const bands = PLAN_LAYERS.map((l) => ({
		...l,
		groups: sortInBand(PLAN_GROUPS.filter((g) => g.layer === l.layer && mapPages(g.key).length)),
	})).filter((b) => b.groups.length);

	return (
		<Card data-testid="plan-block-map">
			<CardContent className="space-y-4 p-6">
				<BlockTitle
					title="页面地图"
					sub="每组菜单管什么、页面之间谁读谁：按菜单分组，从上到下按「定义 → 内容 → 链路 → 评测 → 分析」排；悬停或固定一页，右侧看它的主要能力和依赖，点卡片跳到页面"
					right={<MapLegend />}
				/>
				<div className="flex items-start gap-4">
					<div className="relative min-w-0 flex-1" ref={boxRef} data-testid="plan-map-canvas">
						<div>
							{bands.map((b, i) => (
								<React.Fragment key={b.layer}>
									{i > 0 ? <LayerLink text={bands[i - 1]?.flow ?? ""} layer={b.layer} /> : null}
									<Band label={b.label} layer={b.layer}>
										{b.groups.map((g) => (
											<GroupBox key={g.key} g={g}>
												{mapPages(g.key).map((p) => (
													<PageCard
														key={p.id}
														p={p}
														hl={hlOf(p.id)}
														pinned={pinned === p.id}
														onEnter={enter}
														onLeave={leave}
														onPin={() => setPinned((cur) => (cur === p.id ? null : p.id))}
														cardRef={(el) => {
															if (el) cardRefs.current.set(p.id, el);
															else cardRefs.current.delete(p.id);
														}}
													/>
												))}
											</GroupBox>
										))}
									</Band>
								</React.Fragment>
							))}
						</div>
						<Edges active={active} rel={rel} geo={geo} />
					</div>
					<Detail
						active={active}
						rel={rel}
						pinned={pinned}
						onUnpin={() => setPinned(null)}
						onEnter={hold}
						onLeave={leave}
					/>
				</div>
			</CardContent>
		</Card>
	);
}

/** 地图里画的页：注册表标了 hideInMap 的不画（链路总览本身） */
function mapPages(key: PlanGroup["key"]): PlanPageEntry[] {
	return pagesOfGroup(key).filter((p) => !p.hideInMap);
}

/** 同一层并排的组：被同层别组读的放左边（如评测配置在评测模式左边），其余按菜单顺序 */
function sortInBand(groups: PlanGroup[]): PlanGroup[] {
	const reads = (a: PlanGroup, b: PlanGroup) =>
		pagesOfGroup(a.key).some((p) => p.deps.some((d) => planPage(d.page).group === b.key));
	return [...groups].sort((a, b) => (reads(b, a) ? -1 : reads(a, b) ? 1 : 0));
}

function MapLegend() {
	return (
		<div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
			{(Object.keys(SOURCE_LABEL) as PageSource[]).map((s) => (
				<Badge key={s} tone={SOURCE_TONE[s]}>
					{SOURCE_LABEL[s]}
				</Badge>
			))}
			<span className="inline-flex items-center gap-1">
				<span className="h-3 w-3 rounded-sm ring-2 ring-brand-info" aria-hidden />
				它读的页
			</span>
			<span className="inline-flex items-center gap-1">
				<span className="h-3 w-3 rounded-sm ring-2 ring-brand-warning" aria-hidden />
				读它的页
			</span>
		</div>
	);
}

/** 层与层之间：两枚加粗向下箭头一组居中 ＋ 这一跳的关系；颜色走令牌，跟随明暗主题 */
function LayerLink({ text, layer }: { text: string; layer: number }) {
	return (
		<div
			className="flex items-center justify-center gap-2 py-1.5 pl-[3.25rem]"
			data-testid={`plan-map-flow-${layer}`}
		>
			<span className="flex items-center text-brand-primary" aria-hidden>
				<ChevronsDown className="h-6 w-6" strokeWidth={3} />
				<ChevronsDown className="-ml-2.5 h-6 w-6" strokeWidth={3} />
			</span>
			<span className="text-xs font-medium text-text-secondary">{text}</span>
		</div>
	);
}

/** 一层：左侧层名竖条 ＋ 本层的一级分组并排 */
function Band({
	label,
	layer,
	children,
}: { label: string; layer: number; children: React.ReactNode }) {
	return (
		<div className="flex gap-3" data-testid={`plan-map-band-${layer}`}>
			<div className="flex w-10 shrink-0 items-center justify-center rounded-md bg-surface-subtle text-xs font-medium text-text-secondary">
				<span className="flex flex-col items-center leading-tight">
					{[...label].map((ch) => (
						<span key={ch}>{ch}</span>
					))}
				</span>
			</div>
			<div className="grid min-w-0 flex-1 auto-cols-fr grid-flow-col gap-3">{children}</div>
		</div>
	);
}

function GroupBox({ g, children }: { g: PlanGroup; children: React.ReactNode }) {
	return (
		<div
			className="min-w-0 space-y-2 rounded-lg border border-surface-border bg-surface-background p-3"
			data-testid={`plan-map-group-${g.key}`}
		>
			<GroupHeading g={g} />
			<div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-2">{children}</div>
		</div>
	);
}

/** 高亮只用 box-shadow（ring）；边框宽度、内边距不变，不引起任何位移 */
const HL_RING: Record<Hl, string> = {
	self: "ring-2 ring-brand-accent",
	dep: "ring-2 ring-brand-info",
	user: "ring-2 ring-brand-warning",
	dim: "",
	none: "",
};

function PageCard({
	p,
	hl,
	pinned,
	onEnter,
	onLeave,
	onPin,
	cardRef,
}: {
	p: PlanPageEntry;
	hl: Hl;
	pinned: boolean;
	onEnter: (id: PlanTab) => void;
	onLeave: () => void;
	onPin: () => void;
	cardRef: (el: HTMLElement | null) => void;
}) {
	const r = resolvePage(p.id);
	const body = (
		<>
			<span className="flex items-center gap-1.5 pr-6">
				<span className="truncate text-sm font-semibold">{p.name}</span>
			</span>
			<span className="flex flex-wrap gap-1">
				<Badge tone={SOURCE_TONE[p.source]}>{SOURCE_LABEL[p.source]}</Badge>
				{p.placeholder ? (
					<span title={p.statusNote}>
						<Badge tone="muted">{p.statusNote ? "规划中·无能力与环节" : "规划"}</Badge>
					</span>
				) : null}
			</span>
			<span className="text-xs leading-snug text-text-secondary">{p.answer}</span>
		</>
	);
	const base = cn(
		"flex h-full flex-col gap-1.5 rounded-lg border border-surface-border bg-surface-card px-3 py-2.5 text-left",
		HL_RING[hl],
	);
	return (
		<div
			className={cn(
				"relative transition-opacity duration-150",
				hl === "dim" ? "opacity-40" : "opacity-100",
			)}
			ref={cardRef}
			onMouseEnter={() => onEnter(p.id)}
			onMouseLeave={onLeave}
			data-testid={`plan-map-card-${p.id}`}
			data-hl={hl}
		>
			{r.tab ? (
				<Link
					to="/plan-lab"
					hash={r.tab}
					className={cn(base, "text-text-primary hover:bg-surface-subtle")}
					data-testid={`plan-map-link-${p.id}`}
				>
					{body}
				</Link>
			) : (
				<span
					className={cn(base, "cursor-default border-dashed text-text-muted")}
					title={r.why}
					aria-disabled="true"
				>
					{body}
				</span>
			)}
			<button
				type="button"
				onClick={onPin}
				className={cn(
					"absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors",
					pinned
						? "bg-brand-accent/10 text-brand-accent"
						: "text-text-muted hover:bg-surface-subtle hover:text-text-primary",
				)}
				aria-pressed={pinned}
				aria-label={pinned ? `取消固定「${p.name}」的关系` : `固定显示「${p.name}」的关系`}
				title={pinned ? "取消固定" : "固定显示它的依赖与被依赖"}
				data-testid={`plan-map-pin-${p.id}`}
			>
				{pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
			</button>
		</div>
	);
}

/**
 * 右侧详情区：固定宽高、吸顶常驻，未选中显示引导；选中只换里面的内容（区内滚动），不改变任何卡片的位置。
 * 三段：主要能力（带 ①～⑦，点步号切到运行时流程并高亮那一步）、它读（读什么）、读它（读什么）。
 */
function Detail({
	active,
	rel,
	pinned,
	onUnpin,
	onEnter,
	onLeave,
}: {
	active: PlanTab | null;
	rel: ReturnType<typeof useRelations>;
	pinned: PlanTab | null;
	onUnpin: () => void;
	onEnter: () => void;
	onLeave: () => void;
}) {
	const p = active ? planPage(active) : null;
	const showUnpin = !!pinned && pinned === active;
	return (
		<aside
			className="sticky top-4 flex h-[32rem] w-72 shrink-0 flex-col rounded-lg border border-surface-border bg-surface-background text-xs"
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
			data-testid="plan-map-detail"
		>
			{p && rel ? (
				<>
					<div className="shrink-0 space-y-1 border-b border-surface-border px-3 py-2.5">
						<div className="flex h-5 items-center gap-2">
							<span className="truncate text-sm font-semibold text-text-primary">{p.name}</span>
							<button
								type="button"
								onClick={onUnpin}
								className={cn(
									"ml-auto shrink-0 text-text-muted hover:text-text-primary",
									showUnpin ? "" : "invisible",
								)}
								tabIndex={showUnpin ? 0 : -1}
							>
								取消固定
							</button>
						</div>
					</div>
					<div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-2.5">
						<Answer p={p} />
						<LayerSpread p={p} />
						<Caps p={p} />
						<StepRoles p={p} />
						<RelList
							label="它读"
							tone="text-brand-info"
							items={rel.deps}
							empty="不读其他页面的定义"
							testId="plan-map-detail-deps"
						/>
						<RelList
							label="读它"
							tone="text-brand-warning"
							items={rel.users}
							empty="没有页面读它"
							testId="plan-map-detail-users"
						/>
					</div>
				</>
			) : (
				<div className="flex flex-1 items-center justify-center px-6 text-center leading-5 text-text-muted">
					悬停或点卡片右上角图钉选中一页，这里显示它的主要能力、它读了谁、谁读它。
				</div>
			)}
		</aside>
	);
}

/** 回答什么：注册表里这页的一句话（卡面只留名称和它，详情区给全文） */
function Answer({ p }: { p: PlanPageEntry }) {
	return (
		<section className="space-y-1" data-testid="plan-map-detail-answer">
			<div className="font-medium text-text-primary">回答什么</div>
			<p className="leading-5 text-text-secondary">{p.answer}</p>
		</section>
	);
}

/** 运行时用到的环节：读＝运行时取用，管＝约束这一步的做法或输入（注册表 governs）；点步号切到运行时流程并高亮那一步 */
function StepRoles({ p }: { p: PlanPageEntry }) {
	const navigate = useNavigate();
	const ks = p.steps.filter(isStage);
	if (!ks.length) return null;
	const role = p.governs ? "管" : "读";
	return (
		<section className="space-y-1" data-testid="plan-map-detail-steps">
			<div className="font-medium text-text-primary">
				运行时环节
				<span className="ml-1 font-normal text-text-muted">
					（读＝运行时取用 · 管＝约束这一步的做法或输入）
				</span>
			</div>
			<ul className="flex flex-wrap gap-1">
				{ks.map((k) => {
					const st = stage(k);
					return (
						<li key={k}>
							<button
								type="button"
								onClick={() =>
									navigate({
										to: "/plan-lab",
										search: { view: "runtime", step: k },
										hash: "overview",
									})
								}
								className="inline-flex items-center gap-1 rounded-full bg-surface-subtle px-1.5 py-0.5 text-text-secondary hover:ring-2 hover:ring-brand-accent"
								title={`到运行时流程看第 ${st.no} 步「${st.name}」`}
							>
								<StageNo s={st} className="h-4 w-4" />
								{st.name}
								<span className="font-medium text-brand-info">{role}</span>
							</button>
						</li>
					);
				})}
			</ul>
		</section>
	);
}

/** 层分布的数据源：各自读自己的接口（rules ／ guidance ／ knowledge ／ tools）里每条内容的层字段；L2 的几个子层合并，没有层字段的页不显示这一段 */
function LayerSpread({ p }: { p: PlanPageEntry }) {
	if (p.layerFrom === "rules") return <RulesLayers />;
	if (p.layerFrom === "guidance") return <GuideLayers />;
	if (p.layerFrom === "knowledge") return <KnowledgeLayers />;
	if (p.layerFrom === "tools") return <ToolLayers />;
	return null;
}
const RulesLayers = () => <LayerBars rows={usePlanRules().data?.rules} />;
const GuideLayers = () => <LayerBars rows={usePlanGuidance().data?.guides} />;
const KnowledgeLayers = () => <LayerBars rows={usePlanKnowledge().data?.entries} />;
const ToolLayers = () => (
	<LayerBars rows={usePlanTools().data?.tools.map((t) => ({ layer: t.profile?.layer }))} />
);

function LayerBars({ rows }: { rows: { layer?: string }[] | undefined }) {
	if (!rows) return null;
	const n = (f: (l: string) => boolean) => rows.filter((r) => r.layer && f(r.layer)).length;
	const parts = [
		{ k: "L1", label: "L1", n: n((l) => l === "L1") },
		{ k: "L2", label: "L2", n: n((l) => l.startsWith("L2")) },
		{ k: "L3", label: "L3", n: n((l) => l === "L3") },
		{ k: "env", label: "环境", n: n((l) => l === "环境") },
		{ k: "none", label: "未标", n: rows.filter((r) => !r.layer).length },
	].filter((x) => x.n > 0 || ["L1", "L2", "L3"].includes(x.k));
	return (
		<section className="space-y-1" data-testid="plan-map-detail-layers">
			<div className="font-medium text-text-primary">这页内容的层分布</div>
			<p className="leading-5 text-text-secondary">
				{parts.map((x) => `${x.label} ${x.n} 条`).join(" · ")}
			</p>
		</section>
	);
}

/** 主要能力：读注册表 caps；落在运行时某一步的标步号，点了切到运行时流程并高亮那一步 */
function Caps({ p }: { p: PlanPageEntry }) {
	const navigate = useNavigate();
	const caps = p.caps ?? [];
	const go = (k: StageKey) =>
		navigate({ to: "/plan-lab", search: { view: "runtime", step: k }, hash: "overview" });
	return (
		<section className="space-y-1" data-testid="plan-map-detail-caps">
			<div className="font-medium text-text-primary">主要能力</div>
			{caps.length ? (
				<ul className="space-y-1">
					{caps.map((c) => {
						const st = c.step && isStage(c.step) ? stage(c.step) : null;
						return (
							<li key={c.text} className="flex items-start gap-1.5 leading-5 text-text-secondary">
								{st ? (
									<button
										type="button"
										onClick={() => go(st.key)}
										className="mt-0.5 shrink-0 rounded-full hover:ring-2 hover:ring-brand-accent"
										title={`到运行时流程看第 ${st.no} 步「${st.name}」`}
										aria-label={`到运行时流程看第 ${st.no} 步「${st.name}」`}
										data-testid={`plan-map-detail-step-${st.key}`}
									>
										<StageNo s={st} className="h-4 w-4" />
									</button>
								) : (
									<span className="w-4 shrink-0 text-center text-text-muted" aria-hidden>
										·
									</span>
								)}
								<span>{c.text}</span>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="leading-5 text-text-muted" data-testid="plan-map-detail-status">
					{p.statusNote ?? (p.placeholder ? "规划中" : "这一页的作用见上方说明")}
				</p>
			)}
		</section>
	);
}

const STAGE_KEYS = new Set<string>(STAGES.map((x) => x.key));
const isStage = (k: string): k is StageKey => STAGE_KEYS.has(k);

function RelList({
	label,
	tone,
	items,
	empty,
	testId,
}: {
	label: string;
	tone: string;
	items: { page: PlanTab; what: string }[];
	empty: string;
	testId: string;
}) {
	return (
		<section className="space-y-1" data-testid={testId}>
			<div className={cn("font-medium", tone)}>
				{label} {items.length}
			</div>
			{items.length ? (
				<ul className="space-y-0.5">
					{items.map((d) => {
						const text = `${planPage(d.page).name}：${d.what}`;
						return (
							<li key={d.page} className="truncate leading-5 text-text-secondary" title={text}>
								<span className="text-text-primary">{planPage(d.page).name}</span>
								<span className="text-text-muted">：{d.what}</span>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="leading-5 text-text-muted">{empty}</p>
			)}
		</section>
	);
}

type Line = { key: string; d: string; kind: "dep" | "user" };

/** 高亮时画连线：被读的页 → 读它的页。用缓存的卡片位置算，不触发重排；SVG 绝对定位叠在上层、不吃鼠标 */
function Edges({
	active,
	rel,
	geo,
}: {
	active: PlanTab | null;
	rel: ReturnType<typeof useRelations>;
	geo: { rects: Map<PlanTab, Rect>; w: number; h: number };
}) {
	const lines = React.useMemo<Line[]>(() => {
		if (!active || !rel) return [];
		const path = (from: PlanTab, to: PlanTab) => {
			const a = geo.rects.get(from);
			const c = geo.rects.get(to);
			if (!a || !c) return null;
			if (a.y + a.h <= c.y) {
				const x1 = a.x + a.w / 2;
				const y1 = a.y + a.h;
				const x2 = c.x + c.w / 2;
				const y2 = c.y;
				const dy = Math.max(24, (y2 - y1) / 2);
				return `M${x1},${y1} C${x1},${y1 + dy} ${x2},${y2 - dy} ${x2},${y2}`;
			}
			if (c.y + c.h <= a.y) {
				const x1 = a.x + a.w / 2;
				const y1 = a.y;
				const x2 = c.x + c.w / 2;
				const y2 = c.y + c.h;
				const dy = Math.max(24, (y1 - y2) / 2);
				return `M${x1},${y1} C${x1},${y1 - dy} ${x2},${y2 + dy} ${x2},${y2}`;
			}
			// 同一行：从顶边拱起
			const x1 = a.x + a.w / 2;
			const x2 = c.x + c.w / 2;
			const y = Math.min(a.y, c.y);
			return `M${x1},${y} C${x1},${y - 28} ${x2},${y - 28} ${x2},${y}`;
		};
		const out: Line[] = [];
		for (const d of rel.deps) {
			const p = path(d.page, active);
			if (p) out.push({ key: `d-${d.page}`, d: p, kind: "dep" });
		}
		for (const u of rel.users) {
			const p = path(active, u.page);
			if (p) out.push({ key: `u-${u.page}`, d: p, kind: "user" });
		}
		return out;
	}, [active, rel, geo]);

	return (
		<svg
			className="pointer-events-none absolute inset-0 overflow-visible"
			width={geo.w}
			height={geo.h}
			aria-hidden
			data-testid="plan-map-edges"
		>
			<title>页面依赖连线</title>
			<defs>
				<marker
					id="plan-map-arrow-dep"
					viewBox="0 0 10 10"
					refX="9"
					refY="5"
					markerWidth="7"
					markerHeight="7"
					orient="auto-start-reverse"
				>
					<path d="M0,0 L10,5 L0,10 z" className="fill-brand-info" />
				</marker>
				<marker
					id="plan-map-arrow-user"
					viewBox="0 0 10 10"
					refX="9"
					refY="5"
					markerWidth="7"
					markerHeight="7"
					orient="auto-start-reverse"
				>
					<path d="M0,0 L10,5 L0,10 z" className="fill-brand-warning" />
				</marker>
			</defs>
			{lines.map((l) => (
				<path
					key={l.key}
					d={l.d}
					fill="none"
					strokeWidth={1.75}
					className={l.kind === "dep" ? "stroke-brand-info" : "stroke-brand-warning"}
					markerEnd={`url(#plan-map-arrow-${l.kind})`}
				/>
			))}
		</svg>
	);
}
